import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { buildCheckoutState } from '../components/checkout/utils';
import { toQuery } from '../components/Routes';
import { useConfig } from '../config';
import { ApiCart, ApiTotalsParams } from '../types';
import { Res, useApi, isTransient, reportTemporaryFailure } from './api';
import { UiContainer } from './ui';

const axiosConf = { withCredentials: true };
const cartContractViolationSignatures = new Set<string>();
const cartMissingTotalsSignatures = new Set<string>();

function summarizeCartPayload(payload: unknown): Record<string, unknown> {
	if (!payload || typeof payload !== 'object') {
		return { payloadType: typeof payload };
	}

	const record = payload as Record<string, unknown>;
	const keys = Object.keys(record).sort();
	return {
		keys,
		itemsType: Array.isArray(record.items) ? 'array' : typeof record.items,
		updateErrorsType: Array.isArray(record.updateErrors) ? 'array' : typeof record.updateErrors,
		updateWarningsType: Array.isArray(record.updateWarnings) ? 'array' : typeof record.updateWarnings,
		totalsType:
			record.totals && typeof record.totals === 'object' && !Array.isArray(record.totals)
				? 'object'
				: typeof record.totals,
		successType: typeof record.success,
	};
}

function isCartContractPayload(payload: unknown): payload is ApiCart {
	if (!payload || typeof payload !== 'object') return false;
	const record = payload as Record<string, unknown>;
	return (
		Array.isArray(record.items) &&
		Array.isArray(record.updateErrors) &&
		Array.isArray(record.updateWarnings) &&
		(record.totals === undefined ||
			(!!record.totals && typeof record.totals === 'object' && !Array.isArray(record.totals)))
	);
}

function isCartPayloadMissingTotals(payload: unknown): payload is ApiCart {
	if (!isCartContractPayload(payload)) return false;
	return payload.totals === undefined;
}

function reportCartContractViolation(source: string, payload: unknown): void {
	const summary = summarizeCartPayload(payload);
	const signature = `${source}:${JSON.stringify(summary)}`;
	if (cartContractViolationSignatures.has(signature)) return;
	cartContractViolationSignatures.add(signature);

	Sentry.captureMessage('cart_contract_violation', {
		level: 'error',
		tags: {
			origin: source,
		},
		extra: {
			cartPayloadSummary: summary,
		},
	});
}

function reportCartMissingTotals(source: string, payload: unknown): void {
	const summary = summarizeCartPayload(payload);
	const signature = `${source}:${JSON.stringify(summary)}`;
	if (cartMissingTotalsSignatures.has(signature)) return;
	cartMissingTotalsSignatures.add(signature);

	Sentry.captureMessage('cart_missing_totals', {
		level: 'warning',
		tags: {
			origin: source,
		},
		extra: {
			cartPayloadSummary: summary,
		},
	});
}

function assertValidCartContract(source: string, payload: unknown): ApiCart {
	if (isCartContractPayload(payload)) {
		if (isCartPayloadMissingTotals(payload)) {
			reportCartMissingTotals(source, payload);
		}
		return payload;
	}
	reportCartContractViolation(source, payload);
	throw new Error('Invalid cart contract payload');
}

export function useCart() {
	const { data: maybeCheckout, error: checkoutError } = useApi(Res.checkoutState, {});
	const checkoutState = buildCheckoutState(maybeCheckout ?? {});

	/*
	 * Counted, not yet acted on. The `?? {}` above builds a basket out of nothing, so a checkout
	 * state that merely failed to arrive looks exactly like an empty one.
	 */
	useEffect(() => {
		if (maybeCheckout === undefined && isTransient(checkoutError)) {
			reportTemporaryFailure(
				{
					url: checkoutError.url,
					status: checkoutError.status,
					kind: 'read-as-answer',
					resource: 'checkout-state',
				},
				checkoutError
			);
		}
	}, [checkoutError, maybeCheckout]);
	const ui = UiContainer.useContainer();
	const { priceWithVat } = ui;
	const customerType = priceWithVat ? 'private' : 'company';

	const deliveryNotification =
		checkoutState.delivery.notification === 'call' || checkoutState.delivery.notification === 'confer'
			? checkoutState.delivery.notification
			: '';

	const cartRequestParams: ApiTotalsParams = {
		customerType,
		deliveryType: checkoutState.delivery.type,
		deliveryNotification,
		discountCode: checkoutState.discountCode,
		letterInvoice: checkoutState.payment.type === 'paper',
	};

	const { data, error, mutate } = useApi(Res.cart, cartRequestParams);

	useEffect(() => {
		// `data?.items ?? []` below hands every consumer an empty basket when the cart did not
		// arrive, which is the same misread as the checkout state above
		if (data === undefined && isTransient(error)) {
			reportTemporaryFailure(
				{ url: error.url, status: error.status, kind: 'read-as-answer', resource: 'cart' },
				error
			);
		}
	}, [error, data]);
	const latestMutationIdRef = useRef(0);
	const currentCartContextKeyRef = useRef('');
	const mutateRef = useRef(mutate);
	const { api } = useConfig();
	const contractError = data && !isCartContractPayload(data) ? new Error('Invalid cart contract payload') : undefined;

	if (contractError) {
		reportCartContractViolation('cart.fetch', data);
	} else if (data && isCartPayloadMissingTotals(data)) {
		reportCartMissingTotals('cart.fetch', data);
	}

	const cart: ApiCart = {
		items: data?.items ?? [],
		updateErrors: data?.updateErrors ?? [],
		updateWarnings: data?.updateWarnings ?? [],
		totals: data?.totals,
	};
	const totalsQuery = toQuery({
		customerType: cartRequestParams.customerType,
		deliveryType: cartRequestParams.deliveryType,
		deliveryNotification: cartRequestParams.deliveryNotification,
		discountCode: cartRequestParams.discountCode,
		letterInvoice: cartRequestParams.letterInvoice ? 'true' : 'false',
	});

	useLayoutEffect(() => {
		currentCartContextKeyRef.current = totalsQuery;
	}, [totalsQuery]);

	useLayoutEffect(() => {
		mutateRef.current = mutate;
	}, [mutate]);

	function startCartMutation(): number {
		latestMutationIdRef.current += 1;
		return latestMutationIdRef.current;
	}

	function isLatestCartMutation(mutationId: number): boolean {
		return latestMutationIdRef.current === mutationId;
	}

	function getCartMutationStaleness(
		mutationId: number,
		contextKey: string
	): 'superseded' | 'context-changed' | undefined {
		if (!isLatestCartMutation(mutationId)) return 'superseded';
		if (contextKey !== currentCartContextKeyRef.current) return 'context-changed';
		return undefined;
	}

	return {
		cart,
		error: error ?? contractError,
		currentCount(id: number) {
			return cart.items.find((e) => e.product.id === id)?.quantity ?? 0;
		},
		setQuantity(id: number, quantity: number) {
			const previousCart = cart;
			// update state while we wait for the true values from the server
			const optimisticData = {
				updateErrors: cart.updateErrors,
				updateWarnings: cart.updateWarnings,
				items: cart.items.map((item) => {
					return item.product.id === id ? { ...item, quantity } : item;
				}),
				totals: cart.totals,
			};
			mutate(optimisticData, false);
			const mutationId = startCartMutation();
			const contextKey = totalsQuery;
			void post(mutationId).catch(() => undefined);

			async function post(currentMutationId: number) {
				try {
					const response = await axios.post<ApiCart>(
						`${api}cart/set-quantity.json?${totalsQuery}`,
						{ id, quantity },
						axiosConf
					);
					const nextCart = assertValidCartContract('cart.setQuantity', response.data);
					const staleReason = getCartMutationStaleness(currentMutationId, contextKey);
					if (!staleReason) {
						await mutateRef.current(nextCart, false);
					} else if (staleReason === 'context-changed') {
						await mutateRef.current();
					}
					return nextCart;
				} catch (error) {
					const staleReason = getCartMutationStaleness(currentMutationId, contextKey);
					try {
						if (!staleReason) {
							await mutateRef.current(previousCart, false);
							await mutateRef.current();
						} else if (staleReason === 'context-changed') {
							await mutateRef.current();
						}
					} catch (recoveryError) {
						Sentry.captureException(recoveryError, {
							tags: {
								origin: 'cart.setQuantity.recovery',
							},
							extra: {
								staleReason,
							},
						});
					}
					throw error;
				}
			}
		},
		async setQuantities(payload: { id: number; quantity: number }[]) {
			const mutationId = startCartMutation();
			const contextKey = totalsQuery;
			try {
				const response = await axios.post<ApiCart>(
					`${api}cart/set-quantity.json?${totalsQuery}`,
					payload,
					axiosConf
				);
				const nextCart = assertValidCartContract('cart.setQuantities', response.data);
				const staleReason = getCartMutationStaleness(mutationId, contextKey);
				if (!staleReason) {
					await mutateRef.current(nextCart, false);
				} else if (staleReason === 'context-changed') {
					await mutateRef.current();
				}
			} catch (error) {
				const staleReason = getCartMutationStaleness(mutationId, contextKey);
				try {
					if (!staleReason || staleReason === 'context-changed') {
						await mutateRef.current();
					}
				} catch (recoveryError) {
					Sentry.captureException(recoveryError, {
						tags: {
							origin: 'cart.setQuantities.recovery',
						},
						extra: {
							staleReason,
						},
					});
				}
				throw error;
			}
		},
		async invalidate() {
			mutate();
		},
	};
}
